<template>
  <div class="w-100">
    <loading-spinner v-if="isLoading" />

    <div class="d-flex justify-content-end mb-3">
      <button class="btn btn-sm btn-primary" @click="addNewBudget()">
        <i class="fa-solid fa-plus me-1" /> Add district budget
      </button>
    </div>

    <!-- Summary: per category, the HQ ceiling vs the total allocated across districts -->
    <div class="row g-3 mb-4">
      <div class="col-md-4" v-for="s in categorySummary" :key="s.category">
        <div class="p-3 rounded bg-semi-transparent h-100 border-start border-4"
          :class="s.remaining < 0 ? 'border-danger' : 'border-success'">
          <div class="fw-bold mb-2">{{ s.label }}</div>
          <div class="d-flex justify-content-between small">
            <span class="text-secondary">Yearly HQ Budget</span><span>{{ formatMoney(s.ceiling) }}</span>
          </div>
          <div class="d-flex justify-content-between small">
            <span class="text-secondary">Allocated</span>
            <span>{{ formatMoney(s.allocated) }} <span class="text-secondary">({{ s.pct.toFixed(1) }}%)</span></span>
          </div>
          <hr class="my-2" />
          <div class="d-flex justify-content-between">
            <span>Remaining</span>
            <strong :class="s.remaining < 0 ? 'text-danger' : 'text-success'">{{ formatMoney(s.remaining) }} ({{ s.remainingPct.toFixed(1) }}%)</strong>
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-bordered table-striped table-dark">
        <thead>
          <tr>
            <th class="text-center fit-cell">#</th>
            <th>District</th>
            <th class="text-end">NOD</th>
            <th class="text-end">Emergency</th>
            <th class="text-end">Electrical</th>
            <th class="text-center fit-cell"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(budget, index) in budgets" :key="`budget-row-${budget.id}`">
            <td class="text-center fit-cell">{{ index + 1 }}</td>
            <td>{{ budget.district?.name }}</td>
            <td class="text-end">{{ formatMoney(budget.kjp) }}</td>
            <td class="text-end">{{ formatMoney(budget.emergency) }}</td>
            <td class="text-end">{{ formatMoney(budget.electrical) }}</td>
            <td class="text-center fit-cell text-nowrap">
              <button class="btn btn-sm btn-light me-2" @click="addNewBudget(budget)">
                <i class="fas fa-edit"></i>
              </button>
              <button class="btn btn-sm btn-danger" @click="deleteBudget(budget)">
                <i class="fas fa-trash-alt"></i>
              </button>
            </td>
          </tr>
          <tr v-if="!budgets.length">
            <td colspan="6" class="text-center fst-italic text-secondary">No data found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <add-new-budget-modal
      :show="showAddNewBudgetModal"
      :editing-budget="editingBudget"
      :selected-year="selectedYear"
      :districts="districts"
      @close="closeAddNewBudgetModal"
    />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import LoadingSpinner from "@/components/LoadingSpinner";
import AddNewBudgetModal from "@/components/AddNewBudgetModal";
import { getAmount } from "@/utils/budget";

export default {
  name: "district-split-tab",
  props: ["selectedYear", "lines"],
  components: { LoadingSpinner, AddNewBudgetModal },
  data() {
    return {
      isLoading: false,
      budgets: [],
      districts: [],
      showAddNewBudgetModal: null,
      editingBudget: null,
    };
  },
  watch: {
    selectedYear: {
      handler() {
        this.getBudget();
      },
      immediate: false,
    },
  },
  computed: {
    formatMoney() {
      return (val) => this.Helper.formatMoney(val);
    },
    // Per category: HQ ceiling, total allocated across districts, and remaining.
    categorySummary() {
      const defs = [
        { label: "NOD", category: "wps_nod", column: "kjp" },
        { label: "Emergency", category: "wps_emergency", column: "emergency" },
        { label: "Electrical", category: "wps_electrical", column: "electrical" },
      ];
      return defs.map((d) => {
        const allocated = this.allocated(d.column);
        const ceiling = this.ceiling(d.category);
        return {
          ...d,
          allocated,
          ceiling,
          remaining: ceiling - allocated,
          pct: ceiling > 0 ? (allocated / ceiling) * 100 : 0, // % of HQ budget allocated
          remainingPct: ceiling > 0 ? ((ceiling - allocated) / ceiling) * 100 : 0, // % unallocated
        };
      });
    },
  },
  methods: {
    allocated(column) {
      return this.budgets.reduce((sum, b) => sum + (Number(b[column]) || 0), 0);
    },
    ceiling(category) {
      return getAmount(this.lines, category);
    },
    async getDistricts() {
      const _user = JSON.parse(localStorage.getItem("user") || "{}");
      let url = this.API.endpoints().allDistricts;
      if (_user.districts && _user.districts.length) {
        _user.districts.forEach((district) => {
          url += `&id_in=${district.id}`;
        });
      }
      const [retVal] = await this.Helper.handlePromise(this.API.get(url));
      this.districts = retVal && retVal.data ? retVal.data : [];
    },
    closeAddNewBudgetModal(e) {
      this.showAddNewBudgetModal = false;
      this.editingBudget = null;
      if (e) this.getBudget();
    },
    addNewBudget(budget) {
      if (budget) this.editingBudget = budget;
      this.showAddNewBudgetModal = true;
    },
    async getBudget() {
      this.isLoading = true;
      const year = this.selectedYear || this.Helper.currentYear;
      const [call] = await this.Helper.handlePromise(
        this.API.get(`nod-budgets?year=${year}&_sort=year:DESC`)
      );
      this.budgets = call && call.data ? call.data : [];
      this.isLoading = false;
    },
    async deleteBudget(budget) {
      if (!budget) return;
      const result = await Swal.fire({
        title: "Delete this budget?", icon: "question", showCancelButton: true,
        reverseButtons: true, confirmButtonText: "Delete", confirmButtonColor: "red",
      });
      if (!result.isConfirmed) return;
      this.isLoading = true;
      const [, error] = await this.Helper.handlePromise(
        this.API.del(`nod-budgets/${budget.id}`)
      );
      if (error) {
        console.error(error);
        this.isLoading = false;
        return;
      }
      await this.getBudget();
      Swal.fire("Budget deleted", "", "success");
      this.isLoading = false;
    },
  },
  async mounted() {
    await this.getDistricts();
    await this.getBudget();
  },
};
</script>
